/**
 * Word Replacer Max
 *
 * Declares one web-accessible-resource entry — the whole `assets/` folder — matched to
 * every origin (<all_urls>) with no `use_dynamic_url`. So every bundled image and font
 * under `assets/` is fetchable from any page at a fixed URL.
 *
 * The logo images are the cleanest signal: they are vendor-named
 * (`word-replacer-max-logo_128.png`) and, being images, load through `probeImage` with
 * no CORS grant, so they survive a restrictive page CSP.
 *
 * Manifest verified against version 0.22.0.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'gnemoflnihonmkiacnagnbnlppkamfgo';

export const wordReplacerMax: ExtensionDefinition = {
  id: ID,
  name: 'Word Replacer Max',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 10_000,
  notes:
    'Exposes assets/ to every origin with no dynamic URL. The vendor-named logo PNG is ' +
    'conclusive and loads without a CORS grant.',
  signals: [
    {
      id: 'war-logo-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/img/word-replacer-max-logo_128.png via <img> — a vendor-named logo ' +
        'exposed to every origin; needs no CORS grant, so it works under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/img/word-replacer-max-logo_128.png'),
    },
    {
      id: 'war-wand-icon-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/img/wand-icon.png via <img> — a second vendor-named image, ' +
        'corroborating if the logo is renamed.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/img/wand-icon.png'),
    },
    {
      id: 'war-inter-font',
      kind: 'web-accessible-resource',
      description:
        'Fetch assets/font/InterVariable.ttf — a bundled font, a third path independent ' +
        'of the images.',
      weight: 0.5,
      run: () => probeFetch(ID, 'assets/font/InterVariable.ttf'),
    },
  ],
};
